import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { applicationActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { BfiNumber, LoanNumber } from '../../../common/SharedComponents';
import { ApplicationApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { RefreshEvents } from '../../../common/SharedEvents';
import AppraiserModal from '../../../appraisals/appraisers/modals/AppraiserModal';
import { LoadPanel } from 'devextreme-react/load-panel';
import { BClientOrEntitySelector, BClientOrEntitySelectorType } from '../../../loans/common/LoanComponents';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { formatCurrency, handleAdminError, handleAdminSuccess } from '../../../common/SharedFunctions';
import { ModalSummaryHeader } from '../../common/ApplicationComponents';

const ConvertToLoanModal = () => {
    const { isOpen, id, propertyId = 0 } = useSelector((state) => state.application.convertToLoanModal);
    const { dispatch } = store;

    const today = new Date().setHours(0, 0, 0, 0);

    const [application, set_application] = useState(null);
    const [masterNoteId, set_masterNoteId] = useState(null);

    const [borrowerEntityId, set_borrowerEntityId] = useState(null);
    const [brokerAgreementId, set_brokerAgreementId] = useState(null);
    const [bfiNumber, set_bfiNumber] = useState(null);
    const [loanNumber, set_loanNumber] = useState(null);
    const [approvalDate, set_approvalDate] = useState(today);

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const applicationApi = new ApplicationApi.ApplicationApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            marginRight: '10px',
            marginLeft: '10px',
            textAlign: 'right',
        },
        form: { paddingTop: '10px' },
        formRow: { marginBottom: '0' },
        nonEditable: {
            backgroundColor: '#f7f7f7',
            padding: '5px 10px',
            margin: '5px',
        },
        noPaddingRight: { paddingLeft: '0' },
        padding10Bottom: { marginBottom: '10px' },
        sectionHeader: { marginLeft: '0' },
        option: { paddingBottom: '10px' },
        optionHeader: { marginLeft: '10px' },
        disabledOptionHeader: { marginLeft: '10px', color: 'silver' },
    };

    useEffect(() => {
        async function isOpenChanged() {
            resetState();

            if (!isOpen || !id) {
                return;
            }

            await loadApplication(id);
        }

        isOpenChanged();
    }, [isOpen, id]);

    useEffect(() => {
        if (!application) {
            return;
        }
        if (application.masterNotes && application.masterNotes.length > 0) {
            set_masterNoteId(application.masterNotes[0].id);
            return;
        }
    }, [application]);

    useEffect(() => {
        if (!masterNoteId) {
            return;
        }
        const mn = application.masterNotes.find((a) => a.id === masterNoteId);
        if (mn) {
            if (mn.borrowerEntityId) {
                set_borrowerEntityId(mn.borrowerEntityId);
            }
            if (mn.brokerAgreementId) {
                set_brokerAgreementId(mn.brokerAgreementId);
            }
        } else {
            set_borrowerEntityId(application.borrowerEntityId);
            set_brokerAgreementId(application.brokerAgreementId);
        }
    }, [masterNoteId]);

    const resetState = () => {
        set_application(null);
        set_loadingOpen(false);
        set_masterNoteId(null);
        set_borrowerEntityId(null);
        set_brokerAgreementId(null);
        set_bfiNumber(null);
        set_loanNumber(null);
        set_approvalDate(today);
    };

    const loadApplication = async (id) => {
        try {
            set_loadingMessage('Loading...');
            set_loadingOpen(true);

            const response = await applicationApi.getConvertToLoan(id);
            if (response.statusCode !== 200) {
                set_loadingOpen(false);
                return;
            }

            const appl = response.result;
            set_application(appl);

            appl.properties.forEach((p) => {
                p.name = 'Property ' + p.bfiNumberSuffix;
                if (propertyId && p.id !== propertyId) {
                    p.disabled = true;
                }
            });

            set_bfiNumber(appl.bfiNumber);
            set_loanNumber(appl.loanNumber ?? '');

            if (appl.borrowerEntityId) {
                set_borrowerEntityId(appl.borrowerEntityId);
            }
            if (appl.brokerAgreementId) {
                set_brokerAgreementId(appl.brokerAgreementId);
            }

            set_loadingOpen(false);
        } catch (ex) {
            set_loadingOpen(false);
        }
    };

    const _handleClose = () =>
        dispatch(
            applicationActions.setConvertToLoanModal({
                convertToLoanModal: { isOpen: false },
            })
        );

    const canSubmit = () => {
        if (!application) {
            return false;
        }

        //In order to submit we need:
        //1. Approval Date
        //2. Bfi # if No Linked Loan chosen
        //2. In the case of an LOC loans then a MasterNote is required
        let isOk = approvalDate && bfiNumber;
        if (application.masterNotes && application.masterNotes.length > 0) {
            isOk = isOk && masterNoteId;
        }
        return isOk;
    };

    const submitChanges = async (e) => {
        e.preventDefault();

        try {
            set_loadingMessage('Saving...');
            set_loadingOpen(true);

            const model = {
                id: application.id,
                approvalDate: new Date(approvalDate).toLocaleDateString('en-us'),
                bfiNumber,
                loanNumber,
                masterNoteId,
                borrowerEntityId,
                brokerAgreementId,
            };

            const response = await applicationApi.setConvertToLoan(application.id, model);
            if (response.statusCode !== 200) {
                return;
            }

            refreshEvents.dispatchRefreshEvent();

            handleAdminSuccess('Changes saved successfully.');

            setTimeout(() => {
                _handleClose();
            }, 600);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const onBClientOrEntitySelect = (selectionType, id) => {
        set_borrowerEntityId(id);
        if (selectionType === BClientOrEntitySelectorType.BClient) {
            set_brokerAgreementId(id);
            return;
        }
        if (selectionType === BClientOrEntitySelectorType.Entity) {
            set_brokerAgreementId(null);
            return;
        }
    };

    if (!isOpen || !application) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex='-1'
                            role='dialog'
                            style={{ width: '850px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1 style={{ whiteSpace: 'nowrap' }}>Convert to Loan</h1>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={_handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                                <ModalSummaryHeader application={application} />

                                <form
                                    className='standard-form'
                                    name='rollbackForm'
                                    onSubmit={(event) => submitChanges(event)}
                                >
                                    <div
                                        className='scrollable-content'
                                        style={{ margin: '10px' }}
                                    >
                                        <h2 className='decorated'>Loan</h2>
                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div className='columns small-12 large-3'>
                                                <label htmlFor='orderedDate'>
                                                    Approval Date <span className='req'>*</span>
                                                </label>
                                                <DatePicker
                                                    id='approvalDate'
                                                    selected={approvalDate}
                                                    onChange={set_approvalDate}
                                                    required={true}
                                                />
                                            </div>
                                            <div className='columns small-12 large-3'>
                                                <label htmlFor='bfiNumber'>
                                                    BFI Number <span className='req'>*</span>
                                                </label>
                                                <BfiNumber
                                                    id={'bfiNumber'}
                                                    onChange={set_bfiNumber}
                                                    value={bfiNumber}
                                                    required={true}
                                                />
                                            </div>
                                            <div className='columns small-12 large-3'>
                                                <label htmlFor='loanNumber'>Loan Number</label>
                                                <LoanNumber
                                                    id={'loanNumber'}
                                                    onChange={set_loanNumber}
                                                    value={loanNumber}
                                                    required={false}
                                                />
                                            </div>
                                        </div>

                                        {application?.masterNotes && application.masterNotes.length > 0 && (
                                            <>
                                                <h2 className='decorated'>Master Note</h2>
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='masterNoteId'>
                                                            Master Note
                                                            <span className='req'>*</span>
                                                        </label>
                                                        <select
                                                            id='masterNoteId'
                                                            value={masterNoteId}
                                                            onChange={(e) => set_masterNoteId(e.target.value)}
                                                            disabled={application.masterNotes.length === 1}
                                                        >
                                                            {application.masterNotes.map((mn) => {
                                                                return (
                                                                    <option
                                                                        key={mn.id}
                                                                        value={mn.id}
                                                                    >
                                                                        {mn.bfiNumber} - {formatCurrency(mn.commitment, 0)}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                    </div>
                                                </div>
                                            </>
                                        )}

                                        <h2 className='decorated'>Borrower</h2>
                                        {!masterNoteId && (
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div
                                                    className='columns small-12 large-12'
                                                    style={{
                                                        color: 'darkblue',
                                                        paddingBottom: '5px',
                                                        paddingTop: '5px',
                                                    }}
                                                >
                                                    You can select an existing B-Client or Borrower from the already saved Loan
                                                    Borrowers by searching on the applicants name, email etc, or leave blank to
                                                    create a new one based on the applicants details.
                                                </div>
                                            </div>
                                        )}

                                        <BClientOrEntitySelector
                                            id='agreementOrBorrowerSelect'
                                            borrowerEntityId={borrowerEntityId}
                                            brokerAgreementId={brokerAgreementId}
                                            disabled={masterNoteId}
                                            onSelect={onBClientOrEntitySelect}
                                            required={false}
                                            canEdit={false}
                                        />

                                        <h2 className='decorated'>
                                            {application.properties.length === 1 ? 'Property' : 'Properties'}
                                        </h2>
                                        <ul>
                                            {application.properties.map((p) => {
                                                return (
                                                    <li key={`p${p.id}`}>
                                                        {p.description && (
                                                            <div>
                                                                <b>Property {p.bfiNumberSuffix}</b>
                                                                {p.address && (
                                                                    <span>
                                                                        : {p.address?.street1} {p.address?.street2}{' '}
                                                                        {p.address?.city}, {p.address?.state} {p.address?.zipCode}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>

                                        <div
                                            className='button-footer'
                                            style={styles.buttonContainer}
                                        >
                                            <button
                                                style={styles.buttonMarginRight}
                                                type='submit'
                                                className='button primary'
                                                disabled={!canSubmit()}
                                            >
                                                Submit
                                            </button>
                                            <button
                                                type='button'
                                                className='button hollow'
                                                data-dismiss='modal'
                                                onClick={_handleClose}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <AppraiserModal />
        </>
    );
};

export default ConvertToLoanModal;
